// @ts-strict-ignore
import { isEmpty, isNil } from 'lodash';
import React, { useContext } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { MBHover } from '@shared/components/hover';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1 } from '@shared/components/typography';
import { AddSubformRow } from '@shared/scenes/workflows/edit/components/optionsMenu/SubFormOptionsChildren';
import { useListWorkflowTemplates } from '@shared/scenes/workflows/hooks';
import { isSubformComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { useDebouncedState, useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  workflowTemplate?: MBAPI.WorkflowTemplate;
  onSelect: (subformWorkflowTemplate: MBAPI.WorkflowTemplate) => void;
  navigateToAdd?: () => void;
}

export function SelectSubform({ workflowTemplate, onSelect, navigateToAdd }: Props) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const [query, setQuery] = useDebouncedState('', 300);

  const subformComponents = workflowTemplate?.components?.filter(isSubformComponent) ?? [];
  const workflowTemplateIds = subformComponents.map(c => c.workflowTemplateIds).flat();
  const { workflowTemplates, loading, hasMore, loadMore } = useListWorkflowTemplates({
    skip: isEmpty(workflowTemplateIds),
    input: { ids: workflowTemplateIds, name: isEmpty(query) ? undefined : query },
  });
  const shouldDisplayShadow = workflowTemplates?.length > 5;

  const hasAddSubformRow = !isNil(navigateToAdd) && isDesktop;

  return (
    <>
      <View paddingVertical={2} paddingHorizontal={2}>
        <MBSearchBar
          value={query}
          onChangeText={setQuery}
          placeholder={i18n('workflowTemplate.subformsSearch')}
        />
      </View>
      {loading && (
        <View center style={{ height: 150 }}>
          <MBLoadingIndicator />
        </View>
      )}
      {!loading && isEmpty(workflowTemplates) && (
        <View paddingBottom={1} centerHorizontally paddingHorizontal={2}>
          <View center padding={1}>
            <MBIconV2 name="list" color={colors.accent} size={grid(7)} />
          </View>
          <MBTextBody1 align="center">{i18n('card.blocks.subformsInput.emptyState')}</MBTextBody1>
        </View>
      )}
      <ScrollView style={[shouldDisplayShadow && styles.scrollView]}>
        {workflowTemplates?.map(wft => (
          <MBTouchableOpacity key={wft.id} onPress={() => onSelect(wft)}>
            <MBHover hoverStyle={{ backgroundColor: colors.input.backgroundInactive }}>
              <View row key={wft.id} padding={1} paddingLeft={2} centerVertically>
                <MBIconV2 name="list" color={colors.foregroundActive} size={grid(3)} />
                <View paddingLeft={1} />
                <MBTextBody1 color={colors.foregroundActive}>{wft.name}</MBTextBody1>
              </View>
            </MBHover>
          </MBTouchableOpacity>
        ))}
        {hasMore && (
          <View paddingVertical={3}>
            <MBButton
              title={i18n('card.list.showMore')}
              size="small"
              theme="transparent"
              bold={false}
              borderColor={Colors.v2.gray10}
              onPress={loadMore}
              style={{ alignSelf: 'center' }}
            />
          </View>
        )}
      </ScrollView>
      {hasAddSubformRow && <AddSubformRow onAdd={navigateToAdd} />}
      {!hasAddSubformRow && <View paddingTop={1} />}
    </>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    marginBottom: 0,
  },
});
